import MeetingService from '../../../../services/MeetingService';
import {
  processVideoFile,
  processImageFile,
  processDocumentFile,
  cleanupBlobUrl,
} from '../assetProcessing';
import { getAssetFile, removeAssetFile } from '../assetFileStorage';
import {
  getFileSizeErrorMessage,
  getDurationErrorMessage,
  isFileSizeExceeded,
  isDurationExceeded,
} from '../uploadLimits';
import {
  startUpload,
  queueUpload,
} from '../../store/slices/uploadProgressSlice';
import {
  addAsset,
} from '../../store/slices/assetsSlice';
import {
  addTimelineItem,
} from '../../store/slices/timelineSlice';

export function createUploadHandlers({
  dispatch,
  currentTime,
  selectedItemId,
  currentItem,
  meetingId,
  notifyError,
}) {
  const notifyUploadError = (message) => {
    if (notifyError) {
      notifyError(message);
      return;
    }
    // eslint-disable-next-line no-console
    console.error(message);
  };

  const handleImageUpload = async (files, descriptions = []) => {
    const validFiles = files.filter((file) => {
      if (isFileSizeExceeded(file?.size)) {
        notifyUploadError(getFileSizeErrorMessage(file?.name, file?.size));
        return false;
      }
      return true;
    });
    if (!validFiles.length) {
      return 0;
    }
    const assets = await Promise.all(
      validFiles.map(async (file) => {
        try {
          const asset = await processImageFile(file);
          
          // Match description to file by size (for new files) or name
          const description = descriptions.find(
            (desc) => (desc.size && desc.size === file.size) || desc.name === file.name
          );
          
          // Store description in asset metadata
          if (description) {
            asset.metadata = {
              ...asset.metadata,
              attachmentName: description.name || file.name,
              attachmentDescription: description.description || '',
            };
            // Update asset name if description has a custom name
            if (description.name && description.name !== file.name) {
              asset.name = description.name;
            }
          }
          
          return asset;
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Image import failed', e);
          return null;
        }
      })
    );
    const acceptedAssets = assets.filter(Boolean);
    acceptedAssets.forEach((asset) => {
      dispatch({ ...addAsset({ ...asset, source_type: 'upload' }), meta: { undoable: true } });
      const duration = asset.duration || 5;
      dispatch({
        ...addTimelineItem({
          assetId: asset.id,
          duration,
          currentTime,
          anchorItemId: selectedItemId || currentItem?.id,
        }),
        meta: { undoable: true },
      });
    });
    return acceptedAssets.length;
  };

  const handleDocumentUpload = async (files) => {
    const validFiles = files.filter((file) => {
      if (isFileSizeExceeded(file?.size)) {
        notifyUploadError(getFileSizeErrorMessage(file?.name, file?.size));
        return false;
      }
      return true;
    });
    if (!validFiles.length) {
      return 0;
    }
    const assets = await Promise.all(
      validFiles.map(async (file) => {
        try {
          return await processDocumentFile(file);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error('Document import failed', e);
          return null;
        }
      })
    );
    const acceptedAssets = assets.filter(Boolean);
    acceptedAssets.forEach((asset) => {
      dispatch({ ...addAsset({ ...asset, source_type: 'upload' }), meta: { undoable: true } });
      const duration = asset.duration || 5;
      dispatch({
        ...addTimelineItem({
          assetId: asset.id,
          duration,
          currentTime,
          anchorItemId: selectedItemId || currentItem?.id,
        }),
        meta: { undoable: true },
      });
    });
    return acceptedAssets.length;
  };

  const handleVideoUpload = async (files) => {
    const validFiles = files.filter((file) => {
      if (isFileSizeExceeded(file?.size)) {
        notifyUploadError(getFileSizeErrorMessage(file?.name, file?.size));
        return false;
      }
      return true;
    });
    if (!validFiles.length) {
      return 0;
    }
    const assets = await Promise.all(
      validFiles.map(async (file) => {
        try {
          const asset = await processVideoFile(file);
          if (isDurationExceeded(asset?.duration)) {
            cleanupBlobUrl(asset?.url);
            removeAssetFile(asset?.id);
            notifyUploadError(getDurationErrorMessage(asset?.name || file?.name, asset?.duration));
            return null;
          }
          return asset;
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error('Video import failed', err);
          return null;
        }
      })
    );
    const acceptedAssets = assets.filter(Boolean);
    acceptedAssets.forEach((asset) => {
      dispatch({ ...addAsset({ ...asset, source_type: 'upload' }), meta: { undoable: true } });
      const duration = asset.duration || 0;
      dispatch({
        ...addTimelineItem({
          assetId: asset.id,
          duration,
          currentTime,
          anchorItemId: selectedItemId || currentItem?.id,
        }),
        meta: { undoable: true },
      });

      // Start background upload if meeting exists
      if (meetingId) {
        const storedFile = getAssetFile(asset.id);
        if (storedFile) {
          dispatch(startUpload({ assetId: asset.id, file: storedFile, meetingId })).catch((error) => {
            console.error(`Failed to start background upload for asset ${asset.id}:`, error);
          });
        }
      } else {
        // Queue for upload after meeting is created
        const storedFile = getAssetFile(asset.id);
        if (storedFile) {
          dispatch(queueUpload({ assetId: asset.id, file: storedFile }));
        }
      }
    });
    return acceptedAssets.length;
  };

  const handleLibrarySelection = async (items) => {
    // Ensure we have playable URLs and determine content type; fetch per-item if missing
    const enriched = await Promise.all(
      items.map(async (m) => {
        try {
          const full = await MeetingService.getMeeting(m.id);
          return {
            ...m,
            url: full?.signedS3Url || full?.signedVideoS3Url || '',
            thumbnail_url: m.thumbnail_url || full?.signedThumbnailS3FileKey || '',
            duration: full?.duration || m.duration || 0,
            // Determine if this is an image-only meeting (no video URL, no duration)
            isImageOnly: !full?.signedS3Url && (!full?.duration || full.duration === 0),
          };
        } catch (e) {
          return m; // keep as-is; will show thumbnail
        }
      })
    );

    enriched.forEach((m) => {
      // Determine asset type: image-only meetings become 'image' type
      const isImageContent = m.isImageOnly && m.thumbnail_url;
      const assetType = isImageContent ? 'image' : 'video';
      const assetDuration = isImageContent ? 5 : (m.duration || 0); // Default 5s for images

      const asset = {
        id: `project-${m.id}-${Date.now()}`,
        type: assetType,
        source_type: 'project',
        name: m.title,
        thumbnail_url: m.thumbnail_url || '',
        url: isImageContent ? m.thumbnail_url : (m.url || ''),
        duration: assetDuration,
        metadata: { projectMeetingId: m.id },
      };
      dispatch({ ...addAsset(asset), meta: { undoable: true } });
      dispatch({
        ...addTimelineItem({
          assetId: asset.id,
          duration: asset.duration,
          currentTime,
          anchorItemId: selectedItemId || currentItem?.id,
        }),
        meta: { undoable: true },
      });
    });
  };

  return {
    handleImageUpload,
    handleDocumentUpload,
    handleVideoUpload,
    handleLibrarySelection,
  };
}
